import React, { useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Box,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { IconChevronLeft, IconInfoCircle } from "@tabler/icons-react";
import type { CalendarEventType } from "../../../../../types/calendar";
import type { SalonService } from "../../../../../types/salon";
import type {
  GetTransactionsResponse,
  TransactionResponse,
} from "../../../../../types/transactions";
import { parseApiDateTime } from "../../../../../utils/timeUtils";
import { useSuggestedTimes } from "../../booking/useSuggestedTimes";
import { MAX_SUGGESTIONS, slotLabel, slotParts } from "../detail/detailModel";
import { MOBILE_Z } from "../tokens";
import {
  ToastCopy,
  clockLabel,
  monthDayLabel,
  rebookQuickDates,
} from "./bookingSheetModel";
import { useMobileRebook } from "./useMobileRebook";
import { MobileDateField } from "../sheets/MobileDateField";
import styles from "./MobileBooking.module.css";

export interface MobileRebookScreenProps {
  transaction: GetTransactionsResponse;
  services: SalonService[];
  timezone: string;
  /** Calendar events (all staff) for the conflict note. */
  events: CalendarEventType[];
  onClose: () => void;
  /** Created: store the transactions, move to `day`, show the toast. */
  onRebooked: (
    created: TransactionResponse[],
    day: string,
    toast: ToastCopy,
  ) => void;
}

const ICON = { size: 16, stroke: 1.8 } as const;

/**
 * Rebook, a pushed full screen (README screen 6): back, "Rebook appointment"
 * + source summary, the read-only source card, Pick a date + quick dates,
 * start time, suggested times, the conflict note,
 * and a footer summary with Cancel / Confirm. Date and time are
 * browser-native inputs.
 */
export const MobileRebookScreen: React.FC<MobileRebookScreenProps> = ({
  transaction,
  services,
  timezone,
  events,
  onClose,
  onRebooked,
}) => {
  const rb = useMobileRebook({ transaction, services, timezone, events });

  const client = transaction.client;
  const clientName = client
    ? `${client.first_name ?? ""} ${client.last_name ?? ""}`.trim()
    : "";
  const booked = transaction.line_items?.booked_services || [];
  const serviceNames = rb.rows
    .map((r) => services.find((s) => s.id === r.salonServiceId)?.name)
    .filter((n): n is string => !!n)
    .join(", ");
  const sourceMinutes = rb.totals.minutes;

  // Source "when": the first service's start → the last service's end.
  const firstStart = booked.find((s) => s.start_dt)?.start_dt;
  const ends = booked
    .map((s) => s.calc__end_dt || s.start_dt)
    .filter((s): s is string => !!s)
    .map((s) => parseApiDateTime(s, timezone));
  const sourceWhen = (() => {
    if (!firstStart) return "";
    const start = parseApiDateTime(firstStart, timezone);
    const end = ends.reduce((a, b) => (b.isAfter(a) ? b : a), start);
    return `${start.format("dddd, MMMM D")} · ${start.format("h:mm A")} – ${end.format("h:mm A")}`;
  })();

  const { suggestions } = useSuggestedTimes({
    serviceIds: rb.sugQuery.serviceIds,
    staffIds: rb.sugQuery.staffIds,
    startTime: rb.sugQuery.startTime,
    date: rb.day,
    timezone,
  });
  const shown = suggestions.slice(0, MAX_SUGGESTIONS);

  // Escape = back (Cancel).
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeRef.current();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const handleConfirm = async () => {
    const result = await rb.confirm(clientName);
    if (result) onRebooked(result.created, rb.day, result.toast);
  };

  const summary = rb.time
    ? `${monthDayLabel(rb.day)} · ${clockLabel(rb.time)} · ${sourceMinutes} min`
    : "";

  return (
    <Box
      className={styles.screen}
      style={{ zIndex: MOBILE_Z.pushedScreen }}
      role="dialog"
      aria-label="Rebook appointment"
      data-testid="mobile-rebook-screen"
    >
      <Box className={styles.screenHead}>
        <ActionIcon
          variant="default"
          className={styles.iconBtn}
          onClick={onClose}
          aria-label="Back"
        >
          <IconChevronLeft size={18} stroke={1.8} />
        </ActionIcon>
        <Box className={styles.headText}>
          <Text className={styles.screenTitle}>Rebook appointment</Text>
          <Text className={styles.screenSub}>
            {[clientName, serviceNames].filter(Boolean).join(" · ")}
          </Text>
        </Box>
      </Box>

      <Box className={styles.screenBody}>
        <Box className={styles.sourceCard}>
          <Text className={styles.sourceClient}>{clientName}</Text>
          <Text className={styles.sourceService}>
            {`${serviceNames} · ${sourceMinutes} min`}
          </Text>
          <Text className={styles.sourceWhen}>{sourceWhen}</Text>
        </Box>

        <Box>
          <Text component="label" className={styles.fieldLabel}>
            Pick a date
          </Text>
          <MobileDateField
            label="Pick a date"
            value={rb.day}
            onChange={rb.setDate}
            className={styles.nativeInput}
          />
        </Box>

        <Box className={styles.quickList}>
          {rebookQuickDates(rb.sourceDay).map((q) => (
            <UnstyledButton
              key={q.day}
              className={styles.quickBtn}
              data-on={rb.day === q.day}
              onClick={() => rb.setDate(q.day)}
            >
              {q.label}
            </UnstyledButton>
          ))}
        </Box>

        <Box>
          <Text component="label" className={styles.fieldLabel}>
            Start time
          </Text>
          <TextInput
            type="time"
            step={900}
            aria-label="Start time"
            value={rb.time}
            onChange={(e) => rb.setTime(e.currentTarget.value)}
            classNames={{ input: styles.nativeInput }}
          />
        </Box>

        {shown.length > 0 && (
          <Box className={styles.sugWrap} data-testid="mobile-rebook-suggested">
            <Box className={styles.sugHead}>
              <Text component="span" className={styles.microLabel}>
                Suggested times
              </Text>
            </Box>
            <Box className={styles.sugList}>
              {shown.map((s) => {
                const p = slotParts(s, timezone);
                return (
                  <UnstyledButton
                    key={`${s.start_dt}-${s.staff_service_ids?.[0] ?? ""}`}
                    className={styles.sugChip}
                    data-on={p.day === rb.day && p.time === rb.time}
                    onClick={() => rb.applySuggestion(s)}
                  >
                    {slotLabel(s, timezone)}
                  </UnstyledButton>
                );
              })}
            </Box>
          </Box>
        )}

        {rb.clash && (
          <Box className={styles.warnNote} role="alert">
            <IconInfoCircle size={15} stroke={1.8} />
            <Text component="span" className={styles.conflictText}>
              That slot is already taken for this provider. Pick another time.
            </Text>
          </Box>
        )}

        {rb.error && (
          <Text className={styles.error} role="alert">
            {rb.error}
          </Text>
        )}
      </Box>

      <Box className={styles.footer}>
        <Text className={styles.footHint}>{summary}</Text>
        <UnstyledButton className={styles.ghostBtn} onClick={onClose}>
          Cancel
        </UnstyledButton>
        <UnstyledButton
          className={styles.primaryBtn}
          onClick={() => void handleConfirm()}
          disabled={!rb.bookable || rb.clash || rb.submitting}
        >
          Confirm
        </UnstyledButton>
      </Box>
    </Box>
  );
};
